import { useState } from 'react';
import { X, Tags, Briefcase, Trash2, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { bulkDeleteCandidates } from '@/lib/api';
import { BulkTagDialog } from './BulkTagDialog';
import { BulkAssignJobDialog } from './BulkAssignJobDialog';

interface BulkActionsBarProps {
  selectedIds: string[];
  onClear: () => void;
  onRefresh: () => void;
}

export function BulkActionsBar({ selectedIds, onClear, onRefresh }: BulkActionsBarProps) {
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [assignJobDialogOpen, setAssignJobDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => bulkDeleteCandidates(selectedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      setDeleteDialogOpen(false);
      onClear();
      onRefresh();
    },
  });

  const handleSuccess = () => {
    onClear();
    onRefresh();
  };

  if (selectedIds.length === 0) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-64 right-0 z-30 bg-emerald-600 border-t border-emerald-500 px-6 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-white">
              {selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-white/80 hover:text-white hover:bg-emerald-700"
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setTagDialogOpen(true)}
              className="bg-white/15 text-white hover:bg-white/25 border-0"
            >
              <Tags className="h-4 w-4 mr-1.5" />
              Add Tags
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAssignJobDialogOpen(true)}
              className="bg-white/15 text-white hover:bg-white/25 border-0"
            >
              <Briefcase className="h-4 w-4 mr-1.5" />
              Assign to Job
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="bg-red-500/80 text-white hover:bg-red-500 border-0"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Tag Dialog */}
      <BulkTagDialog
        open={tagDialogOpen}
        onOpenChange={setTagDialogOpen}
        candidateIds={selectedIds}
        onSuccess={handleSuccess}
      />

      {/* Bulk Assign Job Dialog */}
      <BulkAssignJobDialog
        open={assignJobDialogOpen}
        onOpenChange={setAssignJobDialogOpen}
        candidateIds={selectedIds}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Candidates</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''}?
              This action cannot be undone. All associated applications, files, and notes will also be removed.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 my-2">
            <p className="text-sm text-red-400 font-medium">
              Warning: This will permanently delete {selectedIds.length} candidate record{selectedIds.length !== 1 ? 's' : ''} and all related data.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Delete {selectedIds.length} Candidate{selectedIds.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
